/* eslint-disable no-bitwise */
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  changePassword,
  containUserLicense,
  getIsEmployeeATimesheetApprover,
  getMyApproverDetails,
  hasAllPermissions,
  hasAnyPermission,
  hasLicense,
  hasPermission,
  Licenses,
  login,
  logoff,
  testAuth,
} from 'teto-client-api';
import AuthenticatedUser from 'teto-client-api/dist/auth/AuthenticatedUser';
import ChangePasswordResponse from 'teto-client-api/dist/auth/ChangePasswordResponse';
import TestAuthResponse from 'teto-client-api/dist/auth/TestAuthResponse';

export type PersistenceTypes =
  | 'db'
  | 'localStorage'
  | 'sessionStorage'
  | 'none';

export interface AuthContextState {
  authenticated: boolean;
  user?: AuthenticatedUser;
  isAdmin?: boolean;
  isLoaded: boolean;
  appVersion: string;
  // eslint-disable-next-line no-unused-vars
  hasLicense: (license: Licenses) => boolean;
  // eslint-disable-next-line no-unused-vars
  hasEnterpriseLicense: () => boolean;
  // eslint-disable-next-line no-unused-vars
  hasProfessionalLicense: () => boolean;
  // eslint-disable-next-line no-unused-vars
  hasReadOnlyLicense: () => boolean;
  hasAnyLicense: () => boolean;
  persistenceMode: () => PersistenceTypes;
  changePassword: (
    // eslint-disable-next-line no-unused-vars
    oldPassword: string,
    // eslint-disable-next-line no-unused-vars
    newPassword: string,
    // eslint-disable-next-line no-unused-vars
    newPasswordConfirmation: string
  ) => Promise<ChangePasswordResponse>;
  // eslint-disable-next-line no-unused-vars
  hasPermission: (permission: string) => boolean;
  // eslint-disable-next-line no-unused-vars
  hasAnyPermission: (permissions: string[]) => boolean;
  // eslint-disable-next-line no-unused-vars
  hasAllPermissions: (permissions: string[]) => boolean;
  // eslint-disable-next-line no-unused-vars
  // eslint-disable-next-line no-unused-vars
  login: (userName: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  testAuthentication: () => Promise<TestAuthResponse>;
  getUserLicensesList: () => string[];
  // eslint-disable-next-line no-unused-vars
  canApprove: (employeeId: number) => boolean;
  sessionTimeout: boolean;
  disableClockIn: boolean;
}

const PERMISSION_TEST_TIMEOUT = 2000;

const AuthContext = React.createContext<AuthContextState>(null as never);

const AuthContextProvider: React.FC<React.PropsWithChildren<{
  children: React.ReactNode;
}>> = (props) => {
  const [hasAuthenticated, setHasAuthenticated] = useState(false);
  const [user, setUser] = useState<AuthenticatedUser | undefined>(undefined);
  const [isLoaded, setIsLoaded] = useState(false);
  const [disableClockIn, setDisableClockIn] = useState(false);
  const [appVersion, setAppVersion] = useState('');
  const [approvableIds, setApprovableIds] = useState<number[]>([]);
  const [sessionTimeout, setSessionTimeout] = useState<boolean>(false);

  const testAuthTimer = useRef(undefined as any);

  useEffect(() => {
    if (user?.id) {
      getMyApproverDetails()
        .then((d) => {
          setApprovableIds(d.employees);
        });
    } else {
      setApprovableIds([]);
    }
  }, [user?.id]);

  const _isApprover = async (id: number | undefined): Promise<boolean> => {
    if (!id) {
      // eslint-disable-next-line no-console
      console.error('User id not defined');
      return false;
    }
    const result = await getIsEmployeeATimesheetApprover(id);

    return result;
  };

  const _contextTestAuthentication = useCallback(
    () => testAuth()
      .then(async (e) => {
        setDisableClockIn(e.disableClockIn);
        setAppVersion(e.appVersion);
        if (e.user) {
          setHasAuthenticated(true);
          const approverResult = await _isApprover(e.user.id);
          const updatedUser = { ...e.user, isApprover: approverResult };
          setUser(updatedUser);
          setIsLoaded(true);
        } else {
          setHasAuthenticated(false);
          setUser(undefined);
          setIsLoaded(true);
        }
      })
      .catch((e) => {
        setIsLoaded(true);
        setSessionTimeout(true);
        // eslint-disable-next-line no-console
        console.error(e);
      }),
    [],
  );

  useEffect(() => {
    if (testAuthTimer.current) {
      clearTimeout(testAuthTimer.current);
    }

    if (isLoaded && hasAuthenticated) {
      testAuthTimer.current = setTimeout(
        _contextTestAuthentication,
        PERMISSION_TEST_TIMEOUT,
      );
    }
    return () => {
      if (testAuthTimer.current) {
        clearTimeout(testAuthTimer.current);
      }
    };
  }, [isLoaded, hasAuthenticated]);

  useEffect(() => {
    _contextTestAuthentication();
  }, []);

  const _login = (userName: string, password: string) => login({ password, userName })
    .then(async (e) => {
      setHasAuthenticated(true);
      setSessionTimeout(false);
      const approverResult = await _isApprover(e.user.id);
      const updatedUser = { ...e.user, isApprover: approverResult };
      setUser(updatedUser);
    })
    .catch((e) => {
      setHasAuthenticated(false);
      setUser(undefined);
      throw e;
    });

  const _clearAuthDetails = useCallback(() => {
    setHasAuthenticated(false);
    setSessionTimeout(false);
    setUser(undefined);
    if (testAuthTimer.current) {
      clearTimeout(testAuthTimer.current);
    }
  }, []);

  const _logout = () => logoff()
    .then(() => {
      _clearAuthDetails();
    })
    .catch(() => {
      _clearAuthDetails();
    });

  const _changePassword = (
    oldPassword: string,
    newPassword: string,
    newPasswordConfirmation: string,
  ) => changePassword({ oldPassword, newPassword, newPasswordConfirmation });

  const _hasPermission = (permission: string) => (user ? user.id === -1 || hasPermission(user, permission) : false);

  const _hasAnyPermission = (permissions: string[]) => (user ? user.id === -1 || hasAnyPermission(user, permissions) : false);

  const _hasAllPermissions = (permissions: string[]) => (user ? user.id === -1 || hasAllPermissions(user, permissions) : false);

  const _hasLicense = (license: Licenses) => (user && license && user.license ? hasLicense(user, license) : false);

  const _hasEnterpriseLicense = () => (user && user.license
    ? containUserLicense(user, [
      Licenses.TimeEnterprise,
      Licenses.ReadOnlyEnterprise,
      Licenses.TotalETOEnterprise,
    ])
    : false);

  const _hasProfessionalLicense = () => (user && user.license
    ? containUserLicense(user, [
      Licenses.TotalETOProfessional,
      Licenses.ReadOnlyProfessional,
      Licenses.TimeProfessional,
    ])
    : false);

  const _hasReadOnlyLicense = () => (user && user.license
    ? containUserLicense(user, [
      Licenses.ReadOnlyProfessional,
      Licenses.ReadOnlyEnterprise,
    ])
    : false);

  const _getUserLicensesList = () => {
    const results: (string | Licenses)[] = [];

    Object.entries(Licenses).forEach((e) => {
      const value = parseInt(e[0], 10);
      if (
        Number.isInteger(value)
        && e[1] !== 'None'
        && user?.license
        // eslint-disable-next-line no-bitwise
        && (user?.license & value) === value
      ) {
        results.push(e[1]);
      }
    });

    if (results.length === 0) {
      results.push('None');
    }
    return results as string[];
  };

  const _canApprove = useCallback((employeeId: number) => (user?.isApprover && approvableIds.indexOf(employeeId) >= 0) ?? false, [approvableIds, user?.isApprover]);

  const _hasAnyLicense = () => {
    if (!user || !user.license || !user.effectiveLicense) {
      return false;
    }
    return user.license > 0 || user.effectiveLicense > 0;
  };

  const _persistenceMode = () => {
    if (_hasEnterpriseLicense()) {
      return 'db';
    }

    return 'localStorage';
  };

  const { children } = props;

  return (
    <AuthContext.Provider
      value={{
        canApprove: _canApprove,
        appVersion,
        authenticated: hasAuthenticated,
        changePassword: _changePassword,
        login: _login,
        logout: _logout,
        hasPermission: _hasPermission,
        hasAnyPermission: _hasAnyPermission,
        hasAllPermissions: _hasAllPermissions,
        testAuthentication: testAuth,
        hasLicense: _hasLicense,
        hasAnyLicense: _hasAnyLicense,
        hasEnterpriseLicense: _hasEnterpriseLicense,
        hasProfessionalLicense: _hasProfessionalLicense,
        hasReadOnlyLicense: _hasReadOnlyLicense,
        isLoaded,
        user,
        persistenceMode: _persistenceMode,
        getUserLicensesList: _getUserLicensesList,
        isAdmin: Boolean(user?.isAdmin),
        sessionTimeout,
        disableClockIn
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const AuthProvider = AuthContextProvider;
export const AuthConsumer = AuthContext.Consumer;
export default AuthContext;
